import React from 'react';
import { Config, StyleLibrary, exitWebAppToExternalVMPage, isNativeView } from '@vm/omega-shared-component-webapp';
import styled from '@emotion/styled';
import { parseHtml } from '../helpers';
import { ComponentStyle, ProductListItem } from '../../types/types';
import { SecondaryButton } from '../shared/Buttons';

import { TileImage } from './TileImage';

import { DISCLAIMER_TEXT } from './constants';
import { getExternalURL, isDayNoticeAccount } from './utils';

const { mqBreakpoints, pxToRem } = StyleLibrary;

const TileDetailContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(24),
  margin: 0,
  padding: pxToRem(40),
  boxShadow: '0 0 1rem 0 rgb(0, 0, 0, 0.08)',
  opacity: 1,
  backgroundColor: 'var(--color-white)',
  color: 'var(--color-body)',

  [mqBreakpoints.desktop]: {
    margin: `0 ${pxToRem(32)}`,
  },
});

const TileDetailItemsContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',

  button: {
    marginBottom: `${pxToRem(8)}`,
  },

  [mqBreakpoints.desktop]: {
    flexDirection: 'row-reverse',
    gap: pxToRem(32),
    button: {
      marginBottom: 0,
    },
  },
});

const TileDetailTextContainer = styled.div({
  flex: '2 1 0',
});

const LogoContainer = styled.img({
  maxHeight: pxToRem(48),
  maxWidth: '100%',
  marginBottom: pxToRem(24),

  [mqBreakpoints.desktop]: {
    maxHeight: pxToRem(48),
  },
});

const Offer = styled.p({
  color: 'var(--color-primary)',
  paddingBottom: pxToRem(40),
  '&.A': {
    paddingBottom: pxToRem(16),
  },
});

const H2 = styled.h2({
  fontFamily: 'var(--font-heading)',
  color: 'var(--color-black) ',
  fontWeight: 'var(--font-weight-regular)',
  marginBottom: 0,
  paddingBottom: 0,
});

const H4 = styled.h4({
  color: 'var(--color-body)',
  fontWeight: 'var(--font-weight-medium)',
});

const Pad = styled.div({
  paddingBottom: pxToRem(48),
});

const Description = styled.p({
  '&.A': {
    paddingBottom: 0,
  },
});

const Price = styled.div({
  display: 'flex',
  flexDirection: 'row',
  columnGap: pxToRem(16),
  marginBottom: pxToRem(8),

  '& h2': {
    alignSelf: 'center',
  },

  '& p': {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    paddingBottom: 0,
  },
});

const P = styled.p({
  fontWeight: 'var(--font-weight-light)',
});

const Disclaimer = styled.p({
  fontSize: pxToRem(14),
  fontWeight: 'var(--font-weight-light)',
  padding: 0,
  paddingBottom: pxToRem(48),

  '& strong': {
    fontWeight: 'var(--font-weight-medium)',
  },
});

const DisclaimerContainer = styled.div({
  display: 'block',
});

const Title = styled.h6({
  fontFamily: 'Inter',
});

type Props = {
  tileItem: ProductListItem;
  onCtaClick: () => void;
  style: ComponentStyle;
};

const TileDetail: React.FC<Props> = ({ tileItem, onCtaClick, style }) => {
  if (!tileItem) return null;

  const metadata = tileItem?.product?.metadata;

  const title = metadata?.name; // these are swapped around in Java: title <-> name
  const name = metadata?.title; // these are swapped around in Java: title <-> name
  const logo = metadata?.tile?.logo;
  const image = metadata?.tile?.image;
  const description = metadata?.tile?.description;
  const price = metadata?.price;
  const ctaText = metadata?.cta?.buttonText;
  const offerText = metadata?.tile?.offer?.offerText;

  const isExternalRoute = isDayNoticeAccount(name);
  const externalURL = isExternalRoute ? getExternalURL(name) : null;

  const formattedName = name ? name.replace(/\s/g, '-').toLowerCase() : '';

  const onVMTileCtaClick = () => {
    if (isExternalRoute) {
      if (isNativeView()) {
        exitWebAppToExternalVMPage(externalURL);
        return;
      }
      window.open(externalURL, '_blank');
    } else {
      onCtaClick();
    }
  };

  const renderTileDetail = () => {
    if (style === ComponentStyle.A) {
      return (
        <>
          {name && <H4>{name}</H4>}
          {description && (
            <Description className={style} data-testid='mp-tile-description'>
              {parseHtml(description)}
            </Description>
          )}
          {offerText && (
            <Offer className={style} data-testid='mp-tile-offer'>
              {parseHtml(offerText)}
            </Offer>
          )}

          {price && (
            <>
              <Price>
                <H2 className='display-3 numeric' data-testid='mp-tile-amount'>
                  {parseHtml(price.amount)}
                </H2>
                <P data-testid='mp-tile-frequency'>
                  <small>{parseHtml(price.frequency)}</small>
                </P>
              </Price>
              <DisclaimerContainer data-testid='mp-product-disclaimer'>
                <Disclaimer>{parseHtml(DISCLAIMER_TEXT)}</Disclaimer>
              </DisclaimerContainer>
            </>
          )}
          {!price && <Pad />}

          <SecondaryButton onClick={onVMTileCtaClick} data-testid='mp-tile-cta-button' title={ctaText}>
            {ctaText}
          </SecondaryButton>
        </>
      );
    }
    return (
      <>
        {logo && <LogoContainer src={`${Config.get('image_cdn_base_url')}/${logo}`} alt={formattedName} />}
        {title && <Title>{title}</Title>}
        {description && <p data-testid='mp-tile-description'>{parseHtml(description)}</p>}
        {offerText && <Offer data-testid='mp-tile-offer'>{parseHtml(offerText)}</Offer>}
        <SecondaryButton onClick={onCtaClick} data-testid='mp-tile-cta-button' title={ctaText}>
          {ctaText}
        </SecondaryButton>
      </>
    );
  };

  return (
    <TileDetailContainer data-testid={`mp-tile-detail-${formattedName}`}>
      <TileDetailItemsContainer>
        {image && <TileImage image={image} alt={name} />}
        <TileDetailTextContainer>{renderTileDetail()}</TileDetailTextContainer>
      </TileDetailItemsContainer>
    </TileDetailContainer>
  );
};

export default TileDetail;
